import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, MapPin, Navigation, Truck, Package, AlertTriangle, X, Loader2, Camera, Plus, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import PublicLogo from '../components/PublicLogo';
import PublicFeatureNav from '../components/PublicFeatureNav';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  available: 'bg-green-100 text-green-800',
  allocated: 'bg-blue-100 text-blue-800',
  on_the_way: 'bg-amber-100 text-amber-800',
  arrived: 'bg-purple-100 text-purple-800',
  collected: 'bg-teal-100 text-teal-800',
  swarm_departed: 'bg-stone-100 text-stone-600',
  cancelled: 'bg-red-100 text-red-600',
};

const STATUS_STEPS = [
  { key: 'available', label: 'Submitted', icon: Clock, color: 'text-green-600' },
  { key: 'allocated', label: 'Beekeeper Assigned', icon: CheckCircle, color: 'text-blue-600' },
  { key: 'on_the_way', label: 'On the Way', icon: Navigation, color: 'text-amber-600' },
  { key: 'arrived', label: 'Arrived', icon: MapPin, color: 'text-purple-600' },
  { key: 'collected', label: 'Collected', icon: Package, color: 'text-teal-600' },
];

const TERMINAL_STATUSES = ['swarm_departed', 'cancelled', 'unable_to_collect'];

const SITUATION_LABELS = {
  swarm_tree_branch: 'Swarm on tree/branch',
  swarm_fence_post: 'Swarm on fence/post',
  swarm_ground_level: 'Ground-level swarm',
  swarm_elevated: 'Elevated swarm',
  swarm_building_exterior: 'Building exterior',
  bees_in_wall: 'Bees in wall cavity',
  bees_in_roof: 'Bees in roof space',
  bees_in_chimney: 'Bees in chimney',
  bees_in_ground: 'Bees in ground',
  established_colony: 'Established colony',
  other: 'Other',
};

export default function SwarmStatus() {
  const [reportId, setReportId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [report, setReport] = useState(null);
  const [myReports, setMyReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [searchMode, setSearchMode] = useState('phone');
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [bids, setBids] = useState([]);
  const [acceptingBid, setAcceptingBid] = useState(null);
  const fileRef = useRef(null);

  // Check URL for report ID
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setReportId(id);
      fetchStatus(id);
    }
  }, []);

  // Auto-poll for status updates every 15 seconds when viewing a report
  useEffect(() => {
    if (!report || !reportId) return;
    // Don't poll for terminal statuses
    if (['collected', 'swarm_departed', 'cancelled', 'unable_to_collect'].includes(report.status)) return;
    const interval = setInterval(() => fetchStatus(reportId), 15000);
    return () => clearInterval(interval);
  }, [report, reportId]);

  const fetchStatus = async (id) => {
    const rid = id || reportId;
    if (!rid) return toast.error('Please enter a reference number');
    if (rid !== reportId) setReportId(rid);
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/swarm-reports/public/${rid}`);
      setReport(res.data);
      // Sync the list item status so it stays current
      setMyReports(prev => prev.map(r => r.id === rid ? { ...r, status: res.data.status, assigned_to_name: res.data.assigned_to_name, accepted_bid_amount: res.data.accepted_bid_amount } : r));
      // Load bids if paid removal
      if (res.data.status === 'bidding' || res.data.status === 'available') {
        try {
          const bidRes = await axios.get(`${API}/api/swarm-reports/public/${rid}/bids`);
          setBids(bidRes.data || []);
        } catch { setBids([]); }
      }
    } catch {
      setError('Report not found. Please check your reference number.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const [reporterConfirm, setReporterConfirm] = useState(null);

  const handleReporterAction = async (action) => {
    setReporterConfirm(null);
    try {
      await axios.post(`${API}/api/swarm-reports/public/${reportId}/update-status`, { action });
      toast.success(action === 'swarm_departed' ? 'Swarm marked as departed. The beekeeper has been notified.' : 'Report cancelled.');
      fetchStatus(reportId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleSubmitRating = async () => {
    if (!ratingValue) return toast.error('Please select a rating');
    setSubmittingRating(true);
    try {
      await axios.post(`${API}/api/swarm-reports/public/${reportId}/rate`, { rating: ratingValue, comment: ratingComment });
      toast.success('Thank you for your feedback!');
      fetchStatus(reportId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleAcceptBid = async (bidId) => {
    setAcceptingBid(bidId);
    try {
      await axios.post(`${API}/api/swarm-reports/public/${reportId}/accept-bid`, { bid_id: bidId });
      toast.success('Quote accepted! The beekeeper has been notified.');
      fetchStatus(reportId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept quote');
    } finally {
      setAcceptingBid(null);
    }
  };

  const handleAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !reportId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.slice(0, 5).forEach(f => fd.append('photos', f));
      await axios.post(`${API}/api/swarm-reports/public/${reportId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photos added!');
      fetchStatus(reportId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const [cancelling, setCancelling] = useState(false);
  const handleCancelReport = async () => {
    if (!reportId) return;
    if (!window.confirm('Are you sure you want to cancel this request? This cannot be undone.')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/api/swarm-reports/public/${reportId}/cancel`);
      toast.success('Request cancelled successfully');
      fetchStatus(reportId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const getStepIndex = (status) => {
    const idx = STATUS_STEPS.findIndex(s => s.key === status);
    return idx >= 0 ? idx : -1;
  };

  const currentStep = report ? getStepIndex(report.status) : -1;
  const isTerminal = report && TERMINAL_STATUSES.includes(report.status);

  return (
    <div className="min-h-screen honeycomb-bg">
      {/* Nav Bar matching Report Swarm */}
      <header className="backdrop-blur-xl bg-white/80 border-b border-amber-200/60 sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 flex items-center justify-between h-14">
          <PublicLogo size="header" />
          <Link to="/report-swarm" className="text-amber-700 hover:text-amber-800 text-xs font-medium px-3 py-1.5 rounded-full border border-amber-300 hover:bg-amber-50 transition-colors">
            Report a Swarm
          </Link>
        </div>
      </header>

      <div className="max-w-lg mx-auto p-4 space-y-6">
        {/* Page title */}
        <div className="flex items-center gap-4 pt-2">
          <div className="flex-1">
            <h1 className="text-xl font-bold text-stone-800">Track Your Swarm Report</h1>
            <p className="text-stone-500 text-sm mt-1">Check the status of your report and see when a beekeeper is on the way</p>
          </div>
          <PublicLogo size="header" className="hidden sm:block flex-shrink-0" />
        </div>
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            {/* Toggle between phone and reference */}
            <div className="flex gap-1 bg-stone-100 rounded-lg p-1 mb-3">
              <button onClick={() => setSearchMode('phone')} className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-colors ${searchMode === 'phone' ? 'bg-white shadow text-amber-700' : 'text-stone-500'}`}>
                My Phone Number
              </button>
              <button onClick={() => setSearchMode('ref')} className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-colors ${searchMode === 'ref' ? 'bg-white shadow text-amber-700' : 'text-stone-500'}`}>
                Reference Number
              </button>
            </div>

            {searchMode === 'phone' ? (
              <>
                <label className="text-sm font-medium text-stone-700">Enter the phone number you used when reporting</label>
                <div className="flex gap-2 mt-2">
                  <Input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="e.g. 0412 345 678" type="tel" className="flex-1" data-testid="status-phone-input" />
                  <Button onClick={async () => {
                    if (!phoneNumber || phoneNumber.length < 6) return toast.error('Please enter your phone number');
                    setLoading(true); setError(''); setMyReports([]); setReport(null);
                    try {
                      const res = await axios.get(`${API}/api/swarm-reports/public/lookup/by-phone?phone=${encodeURIComponent(phoneNumber)}`);
                      if (res.data?.length > 0) {
                        setMyReports(res.data);
                        if (res.data.length === 1) { fetchStatus(res.data[0].id); }
                      } else { setError('No reports found for this phone number.'); }
                    } catch { setError('Could not find reports. Please try your reference number instead.'); }
                    finally { setLoading(false); }
                  }} disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="status-phone-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <label className="text-sm font-medium text-stone-700">Enter your reference number</label>
                <div className="flex gap-2 mt-2">
                  <Input value={reportId} onChange={e => setReportId(e.target.value)} placeholder="e.g. b5c2ef4c" className="flex-1" data-testid="status-ref-input" />
                  <Button onClick={() => fetchStatus()} disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="status-check-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
                  </Button>
                </div>
              </>
            )}
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </CardContent>
        </Card>

        {/* Multiple reports list — always visible when multiple reports found */}
        {myReports.length > 1 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold text-stone-800 mb-3">Your Reports ({myReports.length})</h3>
              <div className="space-y-2">
                {myReports.map(r => {
                  const isSelected = report && reportId === r.id;
                  return (
                    <button key={r.id} onClick={() => { setReportId(r.id); fetchStatus(r.id); }} className={`w-full text-left p-3 rounded-lg border transition-colors ${isSelected ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-200' : 'border-stone-200 hover:border-amber-400 hover:bg-amber-50'}`} data-testid={`report-item-${r.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0 ${STATUS_COLORS[r.status] || 'bg-stone-100 text-stone-600'}`}>
                            {r.status?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          <span className="text-sm font-medium text-stone-800 truncate">{SITUATION_LABELS[r.situation_type] || r.situation_type}</span>
                          {r.is_paid_removal && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 flex-shrink-0">Paid</span>}
                        </div>
                        <span className="text-xs text-stone-400 flex-shrink-0">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-stone-500">
                        {r.suburb && <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-amber-500" />{r.suburb}</span>}
                        {r.height_estimate && <span>Height: {r.height_estimate}</span>}
                        {r.assigned_to_name && <span className="text-blue-600 font-medium">Beekeeper: {r.assigned_to_name}</span>}
                        {r.accepted_bid_amount && !['available', 'bidding'].includes(r.status) && <span className="text-green-700 font-bold">Agreed: ${r.accepted_bid_amount}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {r.description && <p className="text-xs text-stone-400 truncate flex-1">{r.description}</p>}
                        <span className="text-[10px] text-stone-400 font-mono flex-shrink-0">{r.ref || `SW-${r.id?.slice(0, 6).toUpperCase()}`}</span>
                      </div>
                      {isSelected && <div className="text-[10px] text-amber-600 font-bold mt-1 uppercase tracking-wide">Currently viewing</div>}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status Display */}
        {report && (
          <Card>
            <CardContent className="p-6">
              {/* Report Summary Header */}
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_COLORS[report.status] || 'bg-stone-100'}`}>
                    {report.status?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  <span className="text-sm font-bold text-stone-800">{SITUATION_LABELS[report.situation_type] || report.situation_type}</span>
                  {report.is_paid_removal && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">Paid Removal</span>}
                  <span className="ml-auto text-xs font-mono text-stone-400 bg-white px-2 py-0.5 rounded border border-stone-200">{report.ref || `SW-${report.id?.slice(0, 6).toUpperCase()}`}</span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-stone-500">
                  {report.suburb && <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-amber-500" />{report.suburb}</span>}
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{report.created_at ? new Date(report.created_at).toLocaleString() : '—'}</span>
                </div>
                {report.accepted_bid_amount && !['available', 'bidding'].includes(report.status) && (
                  <div className="mt-2 bg-green-50 border border-green-200 rounded-md px-3 py-1.5 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-sm font-bold text-green-800">Agreed Price: ${report.accepted_bid_amount}</span>
                    {report.accepted_bid_type && <span className="text-xs text-green-600">({report.accepted_bid_type})</span>}
                  </div>
                )}
              </div>
              {isTerminal ? (
                <div className="text-center py-4">
                  <AlertTriangle className="h-12 w-12 text-stone-400 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-stone-700">
                    {report.status === 'swarm_departed' && 'Swarm Has Departed'}
                    {report.status === 'cancelled' && 'Report Cancelled'}
                    {report.status === 'unable_to_collect' && 'Unable to Collect'}
                  </h3>
                  <p className="text-stone-500 mt-2 text-sm">
                    This report is no longer active. If you still need help, please submit a new report.
                  </p>
                  <Button onClick={() => window.location.href = '/report-swarm'} variant="outline" className="mt-4">
                    Report Another Swarm
                  </Button>
                </div>
              ) : (
                <>
                  {/* Progress Steps */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-stone-400">Updates automatically every 15s</span>
                    <Button variant="ghost" size="sm" onClick={() => fetchStatus(reportId)} className="h-7 px-2 text-xs text-amber-700 hover:text-amber-800" data-testid="refresh-status-btn">
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
                    </Button>
                  </div>
                  <div className="space-y-0">
                    {STATUS_STEPS.map((step, idx) => {
                      const isActive = idx === currentStep;
                      const isDone = idx < currentStep;
                      const Icon = step.icon;
                      return (
                        <div key={step.key} className="flex items-start gap-3">
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                              isDone ? 'bg-green-500 border-green-500 text-white' :
                              isActive ? 'bg-amber-500 border-amber-500 text-white animate-pulse' :
                              'bg-white border-stone-300 text-stone-400'
                            }`}>
                              {isDone ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                            </div>
                            {idx < STATUS_STEPS.length - 1 && (
                              <div className={`w-0.5 h-8 ${isDone ? 'bg-green-400' : 'bg-stone-200'}`} />
                            )}
                          </div>
                          <div className={`pt-2 ${isActive ? 'text-amber-800 font-bold' : isDone ? 'text-green-700' : 'text-stone-400'}`}>
                            <span className="text-sm">{step.label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Photo Gallery + Add More */}
                  <div className="mt-6">
                    <h3 className="font-medium text-stone-700 text-sm mb-2 flex items-center gap-2">
                      <Camera className="h-4 w-4" />
                      Photos ({report.photos?.length || 0}/10)
                    </h3>
                    <div className="flex gap-2 flex-wrap">
                      {(report.photos || []).map((p, i) => (
                        <StatusPhotoThumb key={i} path={p} />
                      ))}
                      {(report.photos?.length || 0) < 10 && (
                        <button
                          onClick={() => fileRef.current?.click()}
                          disabled={uploading}
                          className="w-16 h-16 rounded-lg border-2 border-dashed border-amber-400 flex flex-col items-center justify-center text-amber-600 hover:bg-amber-50 transition-colors"
                        >
                          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Plus className="h-5 w-5" /><span className="text-[9px]">Add</span></>}
                        </button>
                      )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={handleAddPhotos} className="hidden" />
                    <p className="text-xs text-stone-400 mt-1">Add more photos to help the beekeeper assess the job</p>
                  </div>

                  {/* Assignment Info */}
                  {report.assigned_to_name && (
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <h3 className="font-bold text-blue-800">Your Beekeeper</h3>
                      <p className="text-blue-700 mt-1">{report.assigned_to_name}</p>
                      {report.estimated_arrival && (
                        <p className="text-blue-600 text-sm mt-1">Estimated arrival: {report.estimated_arrival}</p>
                      )}
                      <p className="text-blue-500 text-xs mt-2">They will contact you to confirm access and arrival.</p>
                    </div>
                  )}

                  {/* Bids for paid removals — Side-by-side comparison */}
                  {bids.length > 0 && ['available', 'bidding'].includes(report.status) && (
                    <div className="mt-6" data-testid="quote-comparison">
                      <h3 className="font-bold text-stone-800 mb-1">Compare Quotes ({bids.filter(b => b.status === 'pending').length})</h3>
                      <p className="text-xs text-stone-500 mb-3">Review and compare quotes from local beekeepers</p>

                      {/* Comparison grid */}
                      <div className={`grid gap-3 ${bids.filter(b => b.status === 'pending').length >= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                        {bids.filter(b => b.status === 'pending').sort((a, b) => a.amount - b.amount).map((b, idx) => (
                          <div key={b.id} className={`relative bg-white border-2 rounded-xl p-4 transition-all hover:shadow-md ${idx === 0 && bids.filter(x => x.status === 'pending').length > 1 ? 'border-green-400 ring-1 ring-green-100' : 'border-stone-200'}`} data-testid={`quote-card-${idx}`}>
                            {/* Best value badge */}
                            {idx === 0 && bids.filter(x => x.status === 'pending').length > 1 && (
                              <span className="absolute -top-2.5 left-3 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Best Price</span>
                            )}

                            {/* Price — prominent */}
                            <div className="text-center mb-3 pt-1">
                              <p className="text-3xl font-black text-green-700">${b.amount.toFixed(0)}</p>
                              <p className="text-[10px] text-stone-500 mt-0.5">{b.price_type === 'fixed' ? 'Fixed price' : b.price_type}{b.estimated_hours > 0 ? ` · ~${b.estimated_hours} hours` : ''}</p>
                            </div>

                            {/* Beekeeper info */}
                            <div className="text-center border-t border-stone-100 pt-2 mb-2">
                              <p className="font-bold text-stone-800 text-sm">{b.user_name}</p>
                              {b.average_rating > 0 && (
                                <div className="flex items-center justify-center gap-1 mt-0.5">
                                  {[1,2,3,4,5].map(s => <span key={s} className={`text-xs ${s <= Math.round(b.average_rating) ? 'text-amber-400' : 'text-stone-200'}`}>★</span>)}
                                  <span className="text-[10px] text-stone-500">({b.total_ratings})</span>
                                </div>
                              )}
                            </div>

                            {/* Features checklist */}
                            <div className="space-y-1 text-xs mb-3">
                              {b.includes_repair && (
                                <div className="flex items-center gap-1.5 text-blue-700">
                                  <CheckCircle className="h-3.5 w-3.5 text-blue-500" />
                                  <span>Includes repair & patching</span>
                                </div>
                              )}
                              {!b.includes_repair && (
                                <div className="flex items-center gap-1.5 text-stone-400">
                                  <X className="h-3.5 w-3.5" />
                                  <span>Repair not included</span>
                                </div>
                              )}
                              {b.estimated_hours > 0 && (
                                <div className="flex items-center gap-1.5 text-stone-600">
                                  <Clock className="h-3.5 w-3.5 text-stone-400" />
                                  <span>Est. {b.estimated_hours} hours</span>
                                </div>
                              )}
                            </div>

                            {/* Description */}
                            {b.description && <p className="text-xs text-stone-600 bg-stone-50 rounded-lg p-2 mb-3 line-clamp-3">{b.description}</p>}

                            <Button onClick={() => handleAcceptBid(b.id)} disabled={acceptingBid === b.id} className="w-full bg-green-600 hover:bg-green-700 text-white text-sm" data-testid={`accept-bid-${idx}`}>
                              {acceptingBid === b.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                              Accept Quote
                            </Button>
                          </div>
                        ))}
                      </div>

                      {bids.filter(b => b.status === 'pending').length >= 2 && (
                        <p className="text-[10px] text-stone-400 text-center mt-2">Sorted by price — lowest first</p>
                      )}
                    </div>
                  )}

                  {report.status === 'available' && (
                    <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                      <Clock className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-amber-800 font-medium">Waiting for a beekeeper to claim this report</p>
                      <p className="text-amber-600 text-sm mt-1">Nearby beekeepers have been notified. This page will update automatically.</p>
                    </div>
                  )}

                  {/* Cancel button — available for open requests */}
                  {['available', 'bidding'].includes(report.status) && (
                    <div className="mt-4 text-center">
                      <button
                        onClick={handleCancelReport}
                        disabled={cancelling}
                        className="text-sm text-stone-400 hover:text-red-500 transition-colors underline"
                        data-testid="cancel-report-btn"
                      >
                        {cancelling ? 'Cancelling...' : 'Someone else is handling it — cancel this request'}
                      </button>
                    </div>
                  )}

                  {report.status === 'cancelled' && (
                    <div className="mt-6 bg-stone-100 border border-stone-200 rounded-xl p-4 text-center">
                      <X className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                      <p className="text-stone-700 font-medium">This request has been cancelled</p>
                      <p className="text-stone-500 text-sm mt-1">Need help again? <Link to="/report-swarm" className="text-amber-600 underline">Report a new swarm</Link></p>
                    </div>
                  )}

                  {/* Rating — shown after collection */}
                  {report.status === 'collected' && (
                    <div className="mt-6 bg-teal-50 border border-teal-200 rounded-xl p-4">
                      {report.rating ? (
                        <div className="text-center">
                          <p className="font-medium text-teal-800">You rated this collection</p>
                          <div className="flex justify-center gap-1 mt-2">
                            {[1,2,3,4,5].map(s => (
                              <span key={s} className={`text-2xl ${s <= report.rating ? 'text-amber-400' : 'text-stone-300'}`}>★</span>
                            ))}
                          </div>
                          <p className="text-teal-700 text-sm mt-1">Thank you for your feedback!</p>
                        </div>
                      ) : (
                        <div>
                          <p className="font-medium text-teal-800 text-center">How was your experience?</p>
                          <p className="text-teal-600 text-xs text-center mt-1">Rate the beekeeper who collected your swarm</p>
                          <div className="flex justify-center gap-2 mt-3">
                            {[1,2,3,4,5].map(s => (
                              <button key={s} onClick={() => setRatingValue(s)} className={`text-3xl transition-transform hover:scale-125 ${s <= ratingValue ? 'text-amber-400' : 'text-stone-300'}`}>
                                ★
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={ratingComment}
                            onChange={e => setRatingComment(e.target.value)}
                            placeholder="Optional: Share your experience..."
                            className="w-full mt-3 p-2 border border-teal-200 rounded-lg text-sm min-h-[60px] focus:ring-2 focus:ring-teal-400"
                          />
                          <Button onClick={handleSubmitRating} disabled={submittingRating || !ratingValue} className="w-full mt-2 bg-teal-600 hover:bg-teal-700 text-white">
                            {submittingRating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Submit Rating
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-stone-400 mt-4 text-center font-mono">
                    {report.ref || `SW-${report.id?.slice(0, 6).toUpperCase()}`}
                  </p>

                  {/* Reporter can update status */}
                  {!['collected', 'swarm_departed', 'cancelled'].includes(report.status) && (
                    <div className="mt-4 pt-4 border-t border-stone-200 space-y-2">
                      <p className="text-xs text-stone-500 text-center">Need to update this report?</p>
                      <div className="flex gap-2">
                        <Button onClick={() => setReporterConfirm('swarm_departed')} variant="outline" className="flex-1 text-amber-700 border-amber-300 text-sm">
                          Swarm Has Left
                        </Button>
                        <Button onClick={() => setReporterConfirm('cancelled')} variant="outline" className="flex-1 text-red-600 border-red-300 text-sm">
                          Cancel Report
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Reporter confirmation dialog */}
        {reporterConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReporterConfirm(null)}>
            <Card className="max-w-sm w-full" onClick={e => e.stopPropagation()}>
              <CardContent className="p-6 text-center">
                <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-stone-800">Are you sure?</h3>
                <p className="text-stone-600 mt-2">
                  {reporterConfirm === 'swarm_departed'
                    ? 'This will notify the beekeeper that the swarm has left and close this report.'
                    : 'This will cancel your report. If a beekeeper is on the way, they will be notified not to attend.'}
                </p>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => setReporterConfirm(null)} variant="outline" className="flex-1">No, Go Back</Button>
                  <Button onClick={() => handleReporterAction(reporterConfirm)} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">
                    Yes, Confirm
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="text-center">
          <Link to="/report-swarm" className="text-amber-600 hover:text-amber-700 text-sm underline">Need to report a new swarm?</Link>
        </div>
      </div>
      <PublicFeatureNav current="/swarm-status" />
    </div>
  );
}

const StatusPhotoThumb = ({ path }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    axios.get(`${API}/api/files/${path}`, { responseType: 'blob' })
      .then(r => setSrc(URL.createObjectURL(r.data)))
      .catch(() => {});
  }, [path]);
  if (!src) return <div className="w-16 h-16 bg-stone-100 rounded-lg animate-pulse" />;
  return <img src={src} alt="" className="w-16 h-16 rounded-lg object-cover border border-stone-200" />;
};
